import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Plane, Clock, MapPin, Car, Utensils, 
  Shield, Info, Star, Terminal, Users, Wifi,
  Coffee, ShoppingBag, Baby, Accessibility
} from 'lucide-react';
import SEOHead, { buildBreadcrumb } from '@/components/SEOHead';
import SiteHeader from '@/components/SiteHeader';
import Breadcrumbs from '@/components/Breadcrumbs';
import AirportInsightsSection from '@/components/AirportInsightsSection';
import NearbyAirports from '@/components/NearbyAirports';
import NotFoundPage from '@/pages/NotFoundPage';
import AirportDelayBadge from '@/components/AirportDelayBadge';
import ParkingWidget from '@/components/ParkingWidget';
import { getFlagshipAnalysis } from '@/lib/flagshipAnalysis';

// Airport data with unique content for each
const airportData = {
  'lax': {
    code: 'LAX',
    name: 'Los Angeles International Airport',
    city: 'Los Angeles',
    state: 'CA',
    description: 'LAX is the largest and busiest airport in the Western United States, serving over 88 million passengers annually. Known for its iconic Theme Building and nine passenger terminals arranged in a horseshoe shape.',
    terminals: [
      { name: 'Terminal 1', airlines: 'Southwest Airlines', tips: 'Often crowded during morning hours. Security lines can be long.' },
      { name: 'Terminal 2', airlines: 'Delta (some flights)', tips: 'Recently renovated with improved amenities.' },
      { name: 'Terminal 3', airlines: 'Delta', tips: 'Connected to Tom Bradley via walkway.' },
      { name: 'Terminal 4', airlines: 'American Airlines', tips: 'One of the busier terminals. Arrive early.' },
      { name: 'Terminal 5', airlines: 'American, JetBlue, Hawaiian', tips: 'Good food options available.' },
      { name: 'Terminal 6', airlines: 'Alaska, United (some)', tips: 'Smaller terminal, usually faster security.' },
      { name: 'Terminal 7', airlines: 'United', tips: 'Connected to Terminal 8.' },
      { name: 'Terminal 8', airlines: 'United', tips: 'Recently upgraded facilities.' },
      { name: 'Tom Bradley International (TBIT)', airlines: 'International carriers', tips: 'Allow extra time - largest terminal with longest walks.' },
    ],
    parking: {
      economy: '$20/day - Remote lot with shuttle service',
      daily: '$40/day - Central Terminal Area',
      hourly: '$6/hour - Close to terminals',
      tips: 'Consider FlyAway bus from Union Station ($9.75) or Van Nuys ($9.75) to avoid parking fees.'
    },
    transportation: [
      'LAX-it Lot: Rideshare pickup location (Uber/Lyft)',
      'FlyAway Bus: Direct service to Union Station, Van Nuys, and more',
      'Metro C Line: Free shuttle to Aviation/LAX station',
      'Rental Cars: Located off-site with shuttle service'
    ],
    dining: [
      { name: 'The Encounter Restaurant', terminal: 'Theme Building', type: 'American', note: 'Iconic space-age restaurant' },
      { name: 'Shake Shack', terminal: 'T3, TBIT', type: 'Burgers', note: 'Popular fast-casual option' },
      { name: 'Urth Caffé', terminal: 'T4', type: 'Coffee/Cafe', note: 'LA favorite for coffee and pastries' },
      { name: "Rock & Brews", terminal: 'T1, T2', type: 'American', note: 'Full bar and rock memorabilia' }
    ],
    securityTips: [
      'TSA PreCheck available in all terminals',
      'CLEAR available in select terminals (T1, T2, T4, T5, TBIT)',
      'Peak hours: 5-9 AM and 4-8 PM',
      'Terminal 1 (Southwest) typically has longest waits',
      'Tom Bradley security can take 30+ minutes during peak international departures'
    ],
    amenities: ['Free WiFi', 'Nursing rooms', 'Pet relief areas', 'Currency exchange', 'Lounges in most terminals'],
    nearbyHotels: ['Hyatt Regency LAX', 'Hilton Los Angeles Airport', 'Sheraton Gateway'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-50 min', extreme: '60+ min' }
  },
  'jfk': {
    code: 'JFK',
    name: 'John F. Kennedy International Airport',
    city: 'New York',
    state: 'NY',
    description: 'JFK is the busiest international air passenger gateway to the United States, handling over 62 million passengers annually. The airport has six operating terminals spread across 5,000 acres in Queens, NYC.',
    terminals: [
      { name: 'Terminal 1', airlines: 'Air France, Japan Airlines, Korean Air, Lufthansa', tips: 'International terminal - allow extra time for customs.' },
      { name: 'Terminal 2', airlines: 'Delta (regional flights)', tips: 'Smaller terminal, less crowded.' },
      { name: 'Terminal 4', airlines: 'Delta (main hub), International carriers', tips: 'Largest terminal - can be a long walk to gates.' },
      { name: 'Terminal 5', airlines: 'JetBlue', tips: 'Modern terminal with excellent amenities and the TWA Hotel.' },
      { name: 'Terminal 7', airlines: 'British Airways, United, Alaska', tips: 'Undergoing renovation - check for changes.' },
      { name: 'Terminal 8', airlines: 'American Airlines', tips: 'Connected to Terminal 7 post-security.' },
    ],
    parking: {
      economy: '$18/day - Long-term lot with AirTrain access',
      daily: '$39/day - Terminal parking',
      hourly: '$6/hour - Short-term parking',
      tips: 'Take AirTrain from Jamaica Station ($8.25) - faster and cheaper than driving/parking.'
    },
    transportation: [
      'AirTrain: Connects all terminals and to LIRR/Subway at Jamaica and Howard Beach',
      'Subway: A train from Howard Beach, E/J/Z from Jamaica Station',
      'LIRR: From Jamaica Station to Penn Station (30 min)',
      'NYC buses: Multiple routes to Queens and Brooklyn'
    ],
    dining: [
      { name: 'Shake Shack', terminal: 'T4', type: 'Burgers', note: 'NYC staple' },
      { name: 'Deep Blue Sushi', terminal: 'T5', type: 'Japanese', note: 'Quality sushi before your flight' },
      { name: 'TWA Hotel Sunken Lounge', terminal: 'T5', type: 'Bar/Lounge', note: 'Iconic mid-century experience' },
      { name: 'Uptown Brasserie', terminal: 'T4', type: 'American', note: 'Full-service restaurant' }
    ],
    securityTips: [
      'TSA PreCheck in all terminals',
      'CLEAR available in T1, T4, T5, T7, T8',
      'Terminal 4 security can be extremely long during evening international rush (4-8 PM)',
      'Terminal 5 (JetBlue) generally has efficient security',
      'Allow extra time for Terminal 1 international departures'
    ],
    amenities: ['Free WiFi', 'TWA Hotel (Terminal 5)', 'Centurion Lounge (T4)', 'Nursing rooms', 'Pet relief'],
    nearbyHotels: ['TWA Hotel', 'JFK Inn', 'Hilton JFK'],
    averageSecurityWait: { low: '10-15 min', medium: '25-35 min', high: '40-55 min', extreme: '60+ min' }
  },
  'ord': {
    code: 'ORD',
    name: "O'Hare International Airport",
    city: 'Chicago',
    state: 'IL',
    description: "O'Hare is one of the world's busiest airports and a major hub for both United and American Airlines. With four domestic terminals and one international terminal, it serves as a critical connection point for travelers across North America.",
    terminals: [
      { name: 'Terminal 1', airlines: 'United Airlines', tips: 'United hub - very large, use moving walkways.' },
      { name: 'Terminal 2', airlines: 'Delta, Air Canada, United (some)', tips: 'Connects to T1 and T3 via underground walkway.' },
      { name: 'Terminal 3', airlines: 'American Airlines', tips: 'American hub - Flagship Lounge available.' },
      { name: 'Terminal 5', airlines: 'International flights, Spirit, Frontier', tips: 'Separate building - allow extra time and use ATS train.' },
    ],
    parking: {
      economy: '$17/day - Lot F with shuttle',
      daily: '$39/day - Terminal parking garages',
      hourly: '$5/hour - Hourly garage',
      tips: 'Blue Line train from downtown ($2.50) is often faster than driving during rush hour.'
    },
    transportation: [
      'CTA Blue Line: Direct service to downtown Chicago (45 min, $2.50)',
      'Metra: North Central Service from nearby stations',
      'Pace Bus: Suburban bus connections',
      'Airport Transit System (ATS): Free train between terminals'
    ],
    dining: [
      { name: 'Tortas Frontera', terminal: 'T1, T3, T5', type: 'Mexican', note: 'Rick Bayless restaurant - local favorite' },
      { name: "Garrett Popcorn", terminal: 'T1, T2, T3', type: 'Snacks', note: 'Chicago classic - get the Chicago Mix' },
      { name: 'Summer House Santa Monica', terminal: 'T3', type: 'American', note: 'Fresh, healthy options' },
      { name: 'Publican Tavern', terminal: 'T3', type: 'American', note: 'Quality beer selection' }
    ],
    securityTips: [
      'TSA PreCheck in all terminals',
      'CLEAR available in T1, T2, T3',
      'Terminal 1 (United) has dedicated PreCheck checkpoint - use it',
      'Allow extra 30 min for Terminal 5 - requires ATS train ride',
      'Morning rush (6-9 AM) is extremely busy'
    ],
    amenities: ['Free WiFi', 'Yoga room (T3)', 'USO Lounge (T2)', 'Kids play areas', 'Pet relief areas'],
    nearbyHotels: ['Hilton Chicago O\'Hare', 'Hyatt Regency O\'Hare', 'Marriott Chicago O\'Hare'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-50 min', extreme: '60+ min' }
  },
  'atl': {
    code: 'ATL',
    name: 'Hartsfield-Jackson Atlanta International Airport',
    city: 'Atlanta',
    state: 'GA',
    description: "The world's busiest airport by passenger traffic, ATL serves as Delta's largest hub. Despite its massive size, the airport is efficiently designed with a central atrium connecting all domestic terminals via underground train.",
    terminals: [
      { name: 'Domestic Terminal', airlines: 'All domestic flights', tips: 'North and South checkpoints - South is usually faster.' },
      { name: 'Concourse T', airlines: 'Delta (main)', tips: 'Closest to main terminal - Delta Sky Club available.' },
      { name: 'Concourse A', airlines: 'Delta', tips: 'Short train ride from main terminal.' },
      { name: 'Concourse B', airlines: 'Delta, Southwest (some)', tips: 'Mix of airlines - good food court.' },
      { name: 'Concourse C', airlines: 'Delta', tips: 'Primarily domestic Delta flights.' },
      { name: 'Concourse D', airlines: 'Delta', tips: 'International departures available.' },
      { name: 'Concourse E', airlines: 'Delta regional', tips: 'Smaller planes - quicker boarding.' },
      { name: 'Concourse F', airlines: 'International', tips: 'International Terminal - longest walk from main.' },
    ],
    parking: {
      economy: '$14/day - Park-Ride lots with shuttle',
      daily: '$36/day - Hourly/Daily lots',
      hourly: '$4/hour - Hourly parking deck',
      tips: 'MARTA train from downtown ($2.50) is excellent - station is inside airport.'
    },
    transportation: [
      'MARTA: Red/Gold lines direct to airport ($2.50, 20 min from downtown)',
      'ATL SkyTrain: Free connection to rental cars and Georgia International Convention Center',
      'Plane Train: Free underground train between all concourses'
    ],
    dining: [
      { name: 'One Flew South', terminal: 'Concourse E', type: 'Southern/Sushi', note: 'Upscale airport dining' },
      { name: 'Chick-fil-A', terminal: 'Multiple', type: 'Fast Food', note: 'Atlanta-based chain (closed Sundays)' },
      { name: 'Paschal\'s', terminal: 'Concourse B', type: 'Southern', note: 'Historic Atlanta restaurant' },
      { name: 'Varasano\'s Pizzeria', terminal: 'Concourse A', type: 'Pizza', note: 'Excellent Neapolitan pizza' }
    ],
    securityTips: [
      'TSA PreCheck at both North and South checkpoints',
      'CLEAR available at main security checkpoints',
      'South checkpoint typically has shorter lines than North',
      'Domestic Terminal only - then take Plane Train to concourses',
      'Peak times: 5-9 AM Monday and Friday afternoons'
    ],
    amenities: ['Free WiFi', 'The Club lounges', 'Interfaith chapel', 'Nursing rooms', 'Pet relief areas'],
    nearbyHotels: ['Atlanta Airport Marriott Gateway', 'Renaissance Atlanta Airport Gateway', 'Hilton Atlanta Airport'],
    averageSecurityWait: { low: '8-12 min', medium: '15-25 min', high: '30-45 min', extreme: '50+ min' }
  },
  'dfw': {
    code: 'DFW',
    name: 'Dallas/Fort Worth International Airport',
    city: 'Dallas',
    state: 'TX',
    description: "DFW is American Airlines' largest hub and one of the busiest airports in the world. The airport is massive - larger than Manhattan - with five terminals connected by the free Skylink train.",
    terminals: [
      { name: 'Terminal A', airlines: 'American Airlines', tips: 'American domestic hub - very large terminal.' },
      { name: 'Terminal B', airlines: 'American Airlines', tips: 'Smaller American terminal.' },
      { name: 'Terminal C', airlines: 'American Airlines', tips: 'American\'s flagship terminal with Admirals Clubs.' },
      { name: 'Terminal D', airlines: 'International carriers, Delta, JetBlue', tips: 'International terminal - Global Entry kiosks available.' },
      { name: 'Terminal E', airlines: 'Frontier, Spirit, Sun Country', tips: 'Budget carriers - usually less crowded.' },
    ],
    parking: {
      economy: '$10/day - Remote lots with shuttle',
      daily: '$27/day - Terminal parking',
      hourly: '$3/hour - Express parking',
      tips: 'Terminal parking fills quickly during holidays. Remote lots are reliable and affordable.'
    },
    transportation: [
      'DART Orange Line: Direct service to downtown Dallas ($3)',
      'TEXRail: Service to Fort Worth ($2.50)',
      'Skylink: Free train connecting all terminals (airside)',
      'Terminal Link: Free bus between terminals (landside)'
    ],
    dining: [
      { name: 'Pappadeaux Seafood Kitchen', terminal: 'Terminal D', type: 'Seafood', note: 'Texas-sized portions' },
      { name: 'Whataburger', terminal: 'Multiple', type: 'Burgers', note: 'Texas fast food institution' },
      { name: 'La Duni Latin Kitchen', terminal: 'Terminal D', type: 'Latin', note: 'Dallas favorite' },
      { name: 'Shinsei', terminal: 'Terminal D', type: 'Asian', note: 'Upscale sushi and Asian fusion' }
    ],
    securityTips: [
      'TSA PreCheck in all terminals',
      'CLEAR available in Terminals A, C, D, E',
      'Terminal D has dedicated international security - can be slow during peaks',
      'Terminals A and C are American hubs - busiest in morning',
      'Use Skylink to switch terminals post-security if needed'
    ],
    amenities: ['Free WiFi', 'Minute Suites (nap rooms)', 'The Club lounges', 'Yoga studio (Terminal D)', 'Pet relief areas'],
    nearbyHotels: ['Grand Hyatt DFW', 'Hyatt Regency DFW', 'Dallas/Fort Worth Airport Marriott'],
    averageSecurityWait: { low: '8-12 min', medium: '18-28 min', high: '35-50 min', extreme: '55+ min' }
  },
  'den': {
    code: 'DEN',
    name: 'Denver International Airport',
    city: 'Denver',
    state: 'CO',
    description: "Recognizable by its iconic white tent-like roof, DEN is the largest airport in the US by land area and a major hub for United and Southwest. The airport features the famous 'Blue Mustang' sculpture and extensive public art.",
    terminals: [
      { name: 'Jeppesen Terminal', airlines: 'All check-in and security', tips: 'Main terminal - security here then train to concourses.' },
      { name: 'Concourse A', airlines: 'Frontier, Southwest, others', tips: 'Closest to main terminal via train.' },
      { name: 'Concourse B', airlines: 'United, Delta, American, JetBlue', tips: 'Mix of major carriers - largest concourse.' },
      { name: 'Concourse C', airlines: 'United', tips: 'United hub - very long walks to end gates.' },
    ],
    parking: {
      economy: '$15/day - Pikes Peak lot with shuttle',
      daily: '$30/day - Garage parking',
      hourly: '$6/hour - Short-term garage',
      tips: 'Airport is 25 miles from downtown. Consider A Line train ($10.50) to avoid long drive and parking fees.'
    },
    transportation: [
      'RTD A Line: Train to/from Union Station downtown (37 min, $10.50)',
      'SkyRide Bus: Express buses to Boulder and other cities',
      'Automated train: Free connection between concourses (airside)',
      'Hotel shuttles: Many hotels offer free airport shuttles'
    ],
    dining: [
      { name: 'Root Down DIA', terminal: 'Concourse C', type: 'American', note: 'Popular Denver restaurant outpost' },
      { name: 'Elway\'s', terminal: 'Concourse B', type: 'Steakhouse', note: 'John Elway\'s upscale steakhouse' },
      { name: 'New Belgium Hub', terminal: 'Concourse B', type: 'Bar/American', note: 'Colorado craft beer' },
      { name: 'Que Bueno! Mexican Grille', terminal: 'Concourse A', type: 'Mexican', note: 'Quick Mexican food' }
    ],
    securityTips: [
      'TSA PreCheck available at both North and South checkpoints',
      'CLEAR available at main security',
      'South security typically less crowded than North',
      'Bridge security (between terminals) is for same-day ticket holders only',
      'Allow 15-20 min to reach distant Concourse C gates via train'
    ],
    amenities: ['Free WiFi', 'Westin DEN hotel (inside airport)', 'Outdoor plaza (weather permitting)', 'Art installations', 'Pet relief areas'],
    nearbyHotels: ['The Westin Denver International Airport', 'Gaylord Rockies', 'Hilton Garden Inn DIA'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-50 min', extreme: '55+ min' }
  },
  'sfo': {
    code: 'SFO',
    name: 'San Francisco International Airport',
    city: 'San Francisco',
    state: 'CA',
    description: "SFO is the largest airport in Northern California and a major hub for United Airlines. Known for its museums, aquarium, and the first airport yoga room in the world. Fog can cause frequent delays, especially in summer.",
    terminals: [
      { name: 'Terminal 1', airlines: 'Southwest, Frontier, JetBlue', tips: 'Budget carriers - Harvey Milk Terminal.' },
      { name: 'Terminal 2', airlines: 'American, Alaska, Virgin', tips: 'Newest terminal - excellent amenities.' },
      { name: 'Terminal 3', airlines: 'United', tips: 'United domestic flights - recently renovated.' },
      { name: 'International Terminal (G)', airlines: 'International carriers', tips: 'Allow extra time - large terminal with long walks.' },
    ],
    parking: {
      economy: '$20/day - Long-term garage',
      daily: '$39/day - Domestic hourly lot',
      hourly: '$6/hour - Domestic hourly',
      tips: 'BART runs directly to airport ($9.65 from downtown). Much faster and cheaper than driving.'
    },
    transportation: [
      'BART: Direct service to downtown SF, Oakland, and beyond',
      'Caltrain: Connect via BART to Millbrae station',
      'SamTrans: Bus service to Peninsula cities',
      'AirTrain: Free connection between terminals and BART/rental cars'
    ],
    dining: [
      { name: 'Cat Cora\'s Kitchen', terminal: 'T2', type: 'Mediterranean', note: 'Iron Chef Cat Cora\'s restaurant' },
      { name: 'Napa Farms Market', terminal: 'T2', type: 'California', note: 'Local, fresh California cuisine' },
      { name: 'Wakaba', terminal: 'Int\'l G', type: 'Japanese', note: 'Authentic Japanese food' },
      { name: 'Bun Mee', terminal: 'T1', type: 'Vietnamese', note: 'Popular Vietnamese sandwiches' }
    ],
    securityTips: [
      'TSA PreCheck in all terminals',
      'CLEAR available in T1, T2, T3, Int\'l',
      'Morning fog can cause delays - check flight status',
      'Terminal 3 United hub is busiest 6-10 AM',
      'International Terminal G requires AirTrain - allow 15+ extra min'
    ],
    amenities: ['Free WiFi', 'SFO Museum (free exhibitions)', 'Yoga room (T2)', 'Aquarium', 'Pet relief areas'],
    nearbyHotels: ['Grand Hyatt at SFO', 'Hyatt Regency SFO', 'The Dylan at SFO'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-45 min', extreme: '50+ min' }
  },
  'mia': {
    code: 'MIA',
    name: 'Miami International Airport',
    city: 'Miami',
    state: 'FL',
    description: "MIA is the largest gateway between the United States and Latin America, serving as American Airlines' primary Latin American hub. The airport handles more international freight than any other US airport.",
    terminals: [
      { name: 'North Terminal (D)', airlines: 'American (main), Qatar, TAP', tips: 'American\'s hub - most domestic flights.' },
      { name: 'Central Terminal (E, F, G)', airlines: 'American, Int\'l carriers', tips: 'International arrivals - immigration here.' },
      { name: 'South Terminal (H, J)', airlines: 'Delta, United, Frontier, Spirit', tips: 'Non-American domestic and international.' },
    ],
    parking: {
      economy: '$13/day - Flamingo garage with MIA Mover',
      daily: '$22/day - Terminal garages',
      hourly: '$5/hour - Short-term parking',
      tips: 'MIA Mover (free people mover) connects to Miami Central Station for Brightline, Metrorail.'
    },
    transportation: [
      'Metrorail: Orange Line to downtown Miami and beyond',
      'Brightline: High-speed train to Fort Lauderdale, West Palm Beach (connect via MIA Mover)',
      'MIA Mover: Free automated train to rental cars and Miami Central',
      'Miami Beach Airport Express: Bus 150 to South Beach'
    ],
    dining: [
      { name: 'La Carreta', terminal: 'Multiple', type: 'Cuban', note: 'Miami Cuban food institution' },
      { name: 'Cibo Wine Bar', terminal: 'Concourse D', type: 'Italian', note: 'Wine and Italian food' },
      { name: 'Casavana Cuban Cuisine', terminal: 'North Terminal', type: 'Cuban', note: 'Authentic Cuban dishes' },
      { name: '305 Pizza', terminal: 'Concourse D', type: 'Pizza', note: 'Quick slice' }
    ],
    securityTips: [
      'TSA PreCheck available in all concourses',
      'CLEAR available in North Terminal',
      'North Terminal (American hub) is busiest - especially early AM Latin America flights',
      'South Terminal generally has shorter waits',
      'International peak: 4-8 PM for Latin America departures'
    ],
    amenities: ['Free WiFi', 'Art exhibitions', 'Currency exchange (multiple)', 'Interfaith chapel', 'Pet relief areas'],
    nearbyHotels: ['MIA Hotel', 'Hilton Miami Airport Blue Lagoon', 'DoubleTree by Hilton Miami Airport'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-50 min', extreme: '55+ min' }
  },
  'sea': {
    code: 'SEA',
    name: 'Seattle-Tacoma International Airport',
    city: 'Seattle',
    state: 'WA',
    description: "Sea-Tac is the Pacific Northwest's largest airport and a major hub for Alaska Airlines and Delta. The airport offers stunning views of Mount Rainier on clear days and is known for its excellent local dining options.",
    terminals: [
      { name: 'Main Terminal', airlines: 'All check-in', tips: 'Central security checkpoint - then walk or train to gates.' },
      { name: 'Concourse A', airlines: 'Alaska Airlines (main)', tips: 'Alaska hub - busy but efficient.' },
      { name: 'Concourse B', airlines: 'Alaska, Delta', tips: 'Connected to A via walkway.' },
      { name: 'Concourse C', airlines: 'American, Southwest, JetBlue', tips: 'Good food options.' },
      { name: 'Concourse D', airlines: 'Delta, Alaska', tips: 'Short walk from main terminal.' },
      { name: 'North Satellite (N)', airlines: 'Alaska, International', tips: 'Take train from main terminal - 5 min ride.' },
      { name: 'South Satellite (S)', airlines: 'International, United', tips: 'Train required - allow extra time.' },
    ],
    parking: {
      economy: '$16/day - Remote lot with shuttle',
      daily: '$35/day - Main garage',
      hourly: '$7/hour - Short-term garage',
      tips: 'Link Light Rail runs 24/7 from downtown Seattle ($3). Highly recommended over driving.'
    },
    transportation: [
      'Link Light Rail: Direct service to downtown Seattle and beyond (38 min, $3)',
      'Sound Transit Bus: Routes to Eastside and South King County',
      'Satellite trains: Free automated trains to N and S satellites (airside)',
      'Rental cars: Accessible via free shuttle'
    ],
    dining: [
      { name: 'Beecher\'s Handmade Cheese', terminal: 'Central Terminal', type: 'American', note: 'Famous Seattle cheese shop - try the mac & cheese' },
      { name: 'Bambuza Vietnam Kitchen', terminal: 'Concourse A', type: 'Vietnamese', note: 'Excellent pho and banh mi' },
      { name: 'Lucky Louie Fish Shack', terminal: 'Concourse C', type: 'Seafood', note: 'Pacific Northwest seafood' },
      { name: 'Starbucks', terminal: 'Multiple', type: 'Coffee', note: 'Seattle\'s original - multiple locations' }
    ],
    securityTips: [
      'TSA PreCheck at main checkpoint',
      'CLEAR available at main security',
      'Central checkpoint can be very busy 4-7 AM',
      'Consider TSA checkpoint 5 (north end) for potentially shorter lines',
      'Allow 15+ min if flying from N or S Satellites (train required)'
    ],
    amenities: ['Free WiFi', 'The Club at SEA lounges', 'Alaska Lounges', 'Kids play areas', 'Pet relief areas'],
    nearbyHotels: ['Hilton Seattle Airport', 'DoubleTree Suites by Hilton Seattle Airport', 'Crowne Plaza Seattle Airport'],
    averageSecurityWait: { low: '8-12 min', medium: '18-28 min', high: '30-45 min', extreme: '50+ min' }
  },
  'las': {
    code: 'LAS',
    name: 'Harry Reid International Airport',
    city: 'Las Vegas',
    state: 'NV',
    description: "LAS is the gateway to Las Vegas and one of the busiest airports in the United States. Known for its slot machines right in the terminal, it serves over 52 million passengers annually. The airport is a major hub for Southwest Airlines and offers easy access to the famous Las Vegas Strip.",
    terminals: [
      { name: 'Terminal 1', airlines: 'Southwest, Delta, American, JetBlue, Alaska, Frontier', tips: 'Southwest dominates - expect crowds. Gates D is the furthest from security.' },
      { name: 'Terminal 3', airlines: 'United, Spirit, International carriers', tips: 'Newer terminal with better amenities. E gates are closest to security.' },
    ],
    parking: {
      economy: '$18/day - Remote lot with 24/7 shuttle',
      daily: '$20-40/day - Terminal garages',
      hourly: '$5/hour - Short-term parking',
      tips: 'Most Strip hotels offer airport shuttles. Rideshare is very affordable in Vegas ($15-25 to Strip).'
    },
    transportation: [
      'Rideshare (Uber/Lyft): Pickup at Level 2 of both terminals, $15-25 to Strip',
      'RTC Bus: Route 109 to Downtown Transit Center ($2)',
      'Rental Cars: Consolidated Rent-A-Car Center with free shuttle',
      'Hotel Shuttles: Many hotels offer complimentary airport transport'
    ],
    dining: [
      { name: 'Shake Shack', terminal: 'T3', type: 'Burgers', note: 'Popular chain with quality burgers' },
      { name: 'La Tapenade', terminal: 'T1 D Gates', type: 'Mediterranean', note: 'Upscale dining option' },
      { name: 'PF Chang\'s', terminal: 'T1', type: 'Asian', note: 'Familiar sit-down option' },
      { name: 'In-N-Out Burger', terminal: 'Near Terminal 1', type: 'Burgers', note: 'West Coast favorite, landside only' }
    ],
    securityTips: [
      'TSA PreCheck available in both terminals',
      'CLEAR available in Terminal 1 and Terminal 3',
      'Sunday evenings are extremely busy (weekend departures)',
      'Terminal 1 is generally busier than Terminal 3',
      'Monday mornings see convention traveler rush'
    ],
    amenities: ['Free WiFi', 'Slot machines', 'The Club LAS lounge', 'Centurion Lounge', 'Art exhibitions'],
    nearbyHotels: ['Hilton Garden Inn Las Vegas City Center', 'Hampton Inn Tropicana', 'Embassy Suites Convention Center'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-50 min', extreme: '55+ min' }
  },
  'mco': {
    code: 'MCO',
    name: 'Orlando International Airport',
    city: 'Orlando',
    state: 'FL',
    description: "MCO is the gateway to Central Florida's world-famous theme parks, including Walt Disney World, Universal Orlando, and SeaWorld. The airport recently opened the new Terminal C, the first major terminal built in the US in 25 years, featuring automated people movers and state-of-the-art technology.",
    terminals: [
      { name: 'Terminal A (Main)', airlines: 'Southwest, JetBlue, American, Spirit, Frontier', tips: 'Original terminal with Gates 1-59. Can be crowded with families.' },
      { name: 'Terminal B (Main)', airlines: 'United, Delta, American, British Airways', tips: 'Gates 60-129. International arrivals processed here.' },
      { name: 'Terminal C', airlines: 'JetBlue (moving), Aer Lingus, Caribbean carriers', tips: 'New 2023 terminal with automated people mover. State-of-the-art amenities.' },
    ],
    parking: {
      economy: '$10/day - North lot with shuttle',
      daily: '$19/day - Terminal garages',
      hourly: '$2/hour - Short-term (first 30 min free)',
      tips: 'Theme parks often offer transportation packages. Consider park shuttles if heading to Disney/Universal.'
    },
    transportation: [
      'Brightline: High-speed train to Miami, West Palm Beach, Fort Lauderdale (Terminal C)',
      'Lynx Bus: Route 11 to downtown Orlando ($2)',
      'SunRail: Connect via Lynx bus to commuter rail',
      'Theme Park Shuttles: Disney\'s Magical Express (ended), Universal SuperStar Shuttle, hotel shuttles'
    ],
    dining: [
      { name: 'Cask & Larder', terminal: 'Terminal B', type: 'Southern', note: 'Orlando favorite with local craft beer' },
      { name: 'Starbucks Reserve', terminal: 'Terminal C', type: 'Coffee', note: 'Premium Starbucks experience' },
      { name: 'Chick-fil-A', terminal: 'Multiple', type: 'Fast Food', note: 'Family favorite (closed Sundays)' },
      { name: 'The Beaumont', terminal: 'Terminal C', type: 'American', note: 'Elevated dining in new terminal' }
    ],
    securityTips: [
      'TSA PreCheck available at all terminals',
      'CLEAR available at Terminal A and B',
      'Family lanes available - helpful with kids',
      'Holiday periods (spring break, summer, Christmas) are extremely busy',
      'Terminal C has most efficient security with newest technology'
    ],
    amenities: ['Free WiFi', 'Kids play areas', 'Art exhibitions', 'The Club MCO', 'Nursing rooms', 'Observation deck'],
    nearbyHotels: ['Hyatt Regency Orlando Airport', 'B Resort at Disney Springs', 'Hilton Orlando'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-50 min', extreme: '55+ min' }
  },
  'clt': {
    code: 'CLT',
    name: 'Charlotte Douglas International Airport',
    city: 'Charlotte',
    state: 'NC',
    description: "CLT is American Airlines' second-largest hub and one of the fastest-growing airports in the country, handling over 53 million passengers a year. Roughly three out of four travelers here are connecting rather than starting their trip in Charlotte, which means crowds arrive in sharp, predictable waves as American's connection banks roll through. The airport's signature white rocking chairs in the central atrium are a Charlotte institution.",
    terminals: [
      { name: 'Concourse A', airlines: 'Delta, United, Southwest, Spirit, Frontier', tips: 'The main non-American concourse. The A expansion gates are a long walk from security — budget 10+ extra minutes.' },
      { name: 'Concourse B', airlines: 'American Airlines', tips: 'Core American domestic gates. Busiest during mid-morning and late-afternoon connection banks.' },
      { name: 'Concourse C', airlines: 'American Airlines', tips: 'More American domestic gates plus some regional flights. Central location makes for short walks.' },
      { name: 'Concourse D', airlines: 'American, Lufthansa, international carriers', tips: 'International gates and customs. Allow extra time for evening transatlantic departures.' },
      { name: 'Concourse E', airlines: 'American Eagle (regional)', tips: 'Largest regional concourse in the American network — a maze of gates at the end of long corridors. Check your gate number early.' },
    ],
    parking: {
      economy: '$8/day - Long Term lots with frequent shuttles',
      daily: '$18/day - Daily decks near terminal',
      hourly: '$4/hour - Hourly deck attached to terminal',
      tips: 'Long Term 1 fills first during holiday weeks. The Express Deck ($14/day) is the best balance of price and walk time.'
    },
    transportation: [
      'CATS Sprinter Bus (Route 5): airport to Uptown Charlotte, about 25 minutes ($2.20)',
      'Rideshare: designated pickup on the lower level, curb zones move quickly',
      'Rental Cars: consolidated facility connected by shuttle',
      'Taxis: flat-ish metered rates to Uptown, roughly $25-30'
    ],
    dining: [
      { name: 'Rhino Market & Deli', terminal: 'Concourse A', type: 'Deli/Local', note: 'Charlotte local favorite for sandwiches' },
      { name: 'Bojangles', terminal: 'Atrium', type: 'Southern Fast Food', note: 'The Carolinas classic — expect a line at breakfast' },
      { name: 'NoDa Brewing', terminal: 'Concourse A', type: 'Brewery', note: 'Local craft beer before your flight' },
      { name: 'Whisky River', terminal: 'Atrium', type: 'American/Bar', note: 'Full bar in the central atrium near the rocking chairs' }
    ],
    securityTips: [
      'TSA PreCheck available at Checkpoint A and the main checkpoints',
      'CLEAR is available at CLT',
      'Peak crush: 5-8 AM as the first American bank departs, and again 3-6 PM',
      'Checkpoint A (far end) often runs shorter lines than the central checkpoints during morning peaks',
      'Because most travelers are connecting, local departure security is calmest mid-morning and after 7 PM'
    ],
    amenities: ['Free WiFi', 'Rocking chairs in the atrium', 'Minute Suites nap pods', 'Nursing rooms', 'American Admirals Clubs', 'Pet relief areas'],
    nearbyHotels: ['Renaissance Charlotte Suites', 'Hilton Charlotte Airport', 'Courtyard Charlotte Airport North'],
    averageSecurityWait: { low: '5-15 min', medium: '15-30 min', high: '30-45 min', extreme: '50+ min' }
  },
  'ewr': {
    code: 'EWR',
    name: 'Newark Liberty International Airport',
    city: 'Newark',
    state: 'NJ',
    description: "EWR is United's East Coast fortress hub and one of the three major airports serving New York City, moving nearly 50 million passengers a year. The brand-new Terminal A (opened 2023) transformed the front half of the airport, while Terminal C remains United's massive transatlantic launchpad. EWR is also the only NYC-area airport with a one-seat rail ride to Manhattan via NJ Transit.",
    terminals: [
      { name: 'Terminal A', airlines: 'American, Air Canada, Delta, JetBlue, United (some)', tips: 'Opened in 2023 — bright, spacious, and the fastest security experience at EWR most hours.' },
      { name: 'Terminal B', airlines: 'International carriers, Delta (some), low-cost carriers', tips: 'Three separate security checkpoints by gate area — check which B gates your flight uses before picking a line.' },
      { name: 'Terminal C', airlines: 'United Airlines (exclusive)', tips: "United's mega-terminal. The 4-8 PM transatlantic bank is the single busiest window — arrive early for evening international departures." },
    ],
    parking: {
      economy: '$29/day - P6 economy lot with shuttle',
      daily: '$44/day - Daily garages P1/P3',
      hourly: '$5.25/half hour - Short-term at terminals',
      tips: 'Parking is among the priciest in the country. NJ Transit + AirTrain from Manhattan is usually faster and cheaper than driving.'
    },
    transportation: [
      'AirTrain Newark: connects all terminals to the Newark Liberty rail station',
      'NJ Transit / Amtrak: one-seat ride to New York Penn Station in about 25 minutes',
      'Rideshare: pickup on terminal arrival levels; expect surcharges to Manhattan',
      'Newark Airport Express Bus: direct coach to Port Authority, Bryant Park and Grand Central'
    ],
    dining: [
      { name: 'The Halal Guys', terminal: 'Terminal A', type: 'Middle Eastern', note: 'NYC street-cart favorite, airport edition' },
      { name: 'Saison', terminal: 'Terminal C', type: 'French Café', note: 'Part of the tablet-ordering food hall United built out' },
      { name: 'Riviera Bar', terminal: 'Terminal C', type: 'Bar/Small plates', note: 'Good spot to wait out the evening international rush' },
      { name: 'Blue Ribbon Sushi', terminal: 'Terminal A', type: 'Sushi', note: 'Outpost of the Manhattan institution' }
    ],
    securityTips: [
      'TSA PreCheck available at all three terminals',
      'CLEAR available at Terminals A and C',
      'Terminal C peaks hard from 4-8 PM with United transatlantic departures',
      'Terminal B lines vary by checkpoint — B1/B2/B3 serve different gates, so verify yours',
      'Morning bank (6-9 AM) hits all terminals; midday (10 AM-2 PM) is the reliable trough'
    ],
    amenities: ['Free WiFi', 'United Polaris Lounge (T C)', 'American Express Centurion Lounge (T A)', 'Nursing rooms', 'Pet relief areas'],
    nearbyHotels: ['Newark Liberty International Airport Marriott (on-site)', 'Courtyard Newark Airport', 'Hilton Newark Airport'],
    averageSecurityWait: { low: '10-15 min', medium: '20-35 min', high: '40-55 min', extreme: '60+ min' }
  },
  'iah': {
    code: 'IAH',
    name: 'George Bush Intercontinental Airport',
    city: 'Houston',
    state: 'TX',
    description: "IAH is United's second-largest hub and the primary international gateway for the US Gulf Coast, serving around 46 million passengers a year across five terminals. It's the main US springboard to Mexico and Latin America, which gives it an unusual double-peak: heavy early-morning domestic banks plus a strong late-evening international push. The five terminals are far apart — terminal changes here take real time.",
    terminals: [
      { name: 'Terminal A', airlines: 'American, Delta, Alaska, Frontier, Spirit', tips: 'The main non-United terminal. Security is usually calmer than the United terminals.' },
      { name: 'Terminal B', airlines: 'United Express (regional)', tips: 'Regional jets dominate. Gates are split across two sides — check B-North vs B-South.' },
      { name: 'Terminal C', airlines: 'United (domestic)', tips: "United's domestic core. Busiest 5-8 AM as the first bank pushes out." },
      { name: 'Terminal D', airlines: 'International carriers (Lufthansa, Emirates, Air France, etc.)', tips: 'All non-United international departures. Evening long-haul bank means 4-7 PM crowds.' },
      { name: 'Terminal E', airlines: 'United (international + some domestic)', tips: 'United long-haul home. Connected to C airside — walkable without re-clearing security.' },
    ],
    parking: {
      economy: '$10/day - Ecopark lots with shuttle',
      daily: '$26/day - Terminal garages',
      hourly: '$4/hour - Terminal garages short-term',
      tips: 'Ecopark 2 shuttles run more frequently than Ecopark 1. Terminal garages fill by mid-morning during spring break and holidays.'
    },
    transportation: [
      'Skyway (airside train): connects all terminals after security — use it instead of exiting',
      'Subway (pre-security underground walkway): connects terminals and the on-site Marriott',
      'METRO Bus Route 102: to downtown Houston (about 50 minutes, $1.25)',
      'Rideshare/Taxi: about 30-45 minutes to downtown depending on traffic'
    ],
    dining: [
      { name: "Pappadeaux Seafood Kitchen", terminal: 'Terminal E', type: 'Cajun/Seafood', note: 'The Houston institution — worth planning a connection around' },
      { name: 'Killen\'s BBQ', terminal: 'Terminal C', type: 'Texas BBQ', note: 'Legit Texas barbecue from the Pearland pitmaster' },
      { name: 'El Real Tex-Mex', terminal: 'Terminal A', type: 'Tex-Mex', note: 'Solid breakfast tacos before the morning bank' },
      { name: 'Common Bond', terminal: 'Terminal D', type: 'Bakery/Café', note: 'Houston favorite for pastries and coffee' }
    ],
    securityTips: [
      'TSA PreCheck available at Terminals A, C, D and E',
      'CLEAR available at Terminals A, C and E',
      'The 5-8 AM United domestic bank is the heaviest security window of the day',
      'Terminal D international check-in counters open ~3.5 hours before long-haul departures and queue early',
      'Connecting between terminals? Stay airside and ride the Skyway — exiting to landside means re-clearing security'
    ],
    amenities: ['Free WiFi', 'United Polaris Lounge (T E)', 'Centurion Lounge (T D)', 'Nursing rooms', 'Interfaith chapels', 'Pet relief areas'],
    nearbyHotels: ['Houston Airport Marriott (on-site, connected by Subway walkway)', 'Hyatt Regency Houston Intercontinental', 'Doubletree IAH'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-50 min', extreme: '60+ min' }
  },
  'phx': {
    code: 'PHX',
    name: 'Phoenix Sky Harbor International Airport',
    city: 'Phoenix',
    state: 'AZ',
    description: "PHX calls itself 'America's Friendliest Airport' and backs it up with one of the smoothest big-airport layouts in the country. It handles close to 50 million passengers a year as a major American Airlines hub and Southwest stronghold. Two quirks matter for planning: Arizona doesn't observe daylight saving time (the airport's offset to your home time zone changes twice a year), and winter is peak season — snowbird and spring-training traffic makes January-April the busiest stretch.",
    terminals: [
      { name: 'Terminal 3', airlines: 'Delta, United, Alaska, Frontier, Spirit, Hawaiian', tips: 'Fully renovated. Security is usually quicker here than Terminal 4.' },
      { name: 'Terminal 4', airlines: 'American, Southwest, British Airways, international carriers', tips: 'Handles about 80% of all PHX traffic across eight concourses. Know your gate letter (A-D on the south, N gates north) before you pick a checkpoint.' },
    ],
    parking: {
      economy: '$14/day - East Economy lots with Sky Train access',
      daily: '$28/day - Terminal garages',
      hourly: '$4/hour - Terminal garages short-term',
      tips: 'East Economy connects directly to the free PHX Sky Train — often faster door-to-gate than the terminal garages on busy mornings.'
    },
    transportation: [
      'PHX Sky Train: free, connects terminals, economy parking and the 44th St Valley Metro Rail station',
      'Valley Metro Rail: light rail to downtown Phoenix and Tempe from 44th St station',
      'Rideshare: designated pickup zones at both terminals',
      'Rental Cars: consolidated Rental Car Center reached by shuttle'
    ],
    dining: [
      { name: "Chelsea's Kitchen", terminal: 'Terminal 4', type: 'American/Southwest', note: 'Local favorite — one of the best airport restaurants in the US' },
      { name: 'Barrio Café', terminal: 'Terminal 4', type: 'Mexican', note: 'Acclaimed chef Silvana Salcido Esparza\'s airport outpost' },
      { name: 'Four Peaks Brewing', terminal: 'Terminal 4', type: 'Brewery', note: 'Tempe craft brewery — try the Kilt Lifter' },
      { name: 'Matt\'s Big Breakfast', terminal: 'Terminal 4', type: 'Breakfast', note: 'Phoenix breakfast institution, perfect before an early bank' }
    ],
    securityTips: [
      'TSA PreCheck available at Terminal 3 and Terminal 4 checkpoints',
      'CLEAR available at Terminal 4',
      'Terminal 4 south checkpoints (American) peak 5-8 AM; Southwest N-gate checkpoints peak slightly later',
      'Winter (Jan-Apr) is high season — add 15 minutes to normal expectations',
      'Summer monsoon afternoons can cause delay waves that back up the evening checkpoints'
    ],
    amenities: ['Free WiFi', 'PHX Sky Train between terminals', 'Airport art museum program', 'Nursing rooms', 'Pet parks', 'USO center'],
    nearbyHotels: ['Sheraton Phoenix Airport', 'Aloft Phoenix Airport', 'Hampton Inn Phoenix Airport North'],
    averageSecurityWait: { low: '5-15 min', medium: '15-25 min', high: '30-45 min', extreme: '50+ min' }
  },
  'bos': {
    code: 'BOS',
    name: 'Boston Logan International Airport',
    city: 'Boston',
    state: 'MA',
    description: "Boston Logan is New England's dominant airport, serving over 40 million passengers a year just four miles from downtown Boston. It's a JetBlue focus city and Delta hub with a fast-growing transatlantic schedule. Logan's four terminals sit on a compact loop, and its public transit story is unusually good for a US airport — the free Silver Line bus runs straight to South Station downtown.",
    terminals: [
      { name: 'Terminal A', airlines: 'Delta', tips: 'Delta\'s renovated home. Connected to Terminal E via post-security walkway for international connections.' },
      { name: 'Terminal B', airlines: 'American, Alaska, United, Southwest, Spirit', tips: 'Two separate piers with separate checkpoints — verify whether your gate is B1 (American side) or B2 (United side).' },
      { name: 'Terminal C', airlines: 'JetBlue, Cape Air', tips: 'JetBlue\'s base. The 6-9 AM JetBlue bank is the busiest domestic window at Logan.' },
      { name: 'Terminal E', airlines: 'International carriers + JetBlue international', tips: 'All international departures. The 5-9 PM transatlantic bank makes early evening the peak of the whole airport.' },
    ],
    parking: {
      economy: '$29/day - Economy garage with shuttle',
      daily: '$42/day - Central Parking / terminal garages',
      hourly: '$10/hour - Short-term in Central Parking',
      tips: 'Logan parking sells out on holiday weekends — reserve online in advance. The Blue Line + water taxi options beat driving during Boston rush hour.'
    },
    transportation: [
      'Silver Line SL1: FREE bus from all terminals to South Station (Red Line connection)',
      'Blue Line: Airport station via free Massport shuttle, quickest route to downtown/East Boston',
      'Logan Express: coach service to Framingham, Braintree, Woburn, Peabody and Back Bay',
      'Water taxi / ferry: to the downtown waterfront — the most scenic airport arrival in America'
    ],
    dining: [
      { name: 'Legal Sea Foods', terminal: 'Terminals A, B, C', type: 'Seafood', note: 'The Boston classic — clam chowder before you board' },
      { name: 'Sal\'s Pizza', terminal: 'Terminal C', type: 'Pizza', note: 'Big New England slices, fast line' },
      { name: 'Boston Beer Works', terminal: 'Terminal C', type: 'Brewery', note: 'Local beers near the JetBlue gates' },
      { name: 'Dunkin\'', terminal: 'Every terminal', type: 'Coffee', note: 'It\'s Boston — there\'s one near every gate cluster' }
    ],
    securityTips: [
      'TSA PreCheck available at all terminals',
      'CLEAR available at Terminals A, C and E',
      'Terminal C peaks 6-9 AM with the JetBlue morning bank',
      'Terminal E peaks 5-9 PM with transatlantic departures — arrive 3 hours early in summer',
      'Terminal B has two independent checkpoints; the wrong one adds a 15-minute landside walk'
    ],
    amenities: ['Free WiFi', 'Free Silver Line to downtown', 'Nursing rooms', 'Pet relief areas', 'Lounges in every terminal', 'Public art program'],
    nearbyHotels: ['Hilton Boston Logan (connected by walkway)', 'Hyatt Regency Boston Harbor', 'Embassy Suites Boston Logan'],
    averageSecurityWait: { low: '10-15 min', medium: '20-30 min', high: '35-50 min', extreme: '55+ min' }
  }
};

// Additional airports with basic info
const additionalAirports = {
  'phl': { code: 'PHL', name: 'Philadelphia International Airport', city: 'Philadelphia', state: 'PA', description: 'American Airlines hub serving the Philadelphia region. Known for cheesesteak options and good regional rail connections.' },
  'msp': { code: 'MSP', name: 'Minneapolis-Saint Paul International Airport', city: 'Minneapolis', state: 'MN', description: 'Delta\'s second-largest hub with two terminals. Well-designed airport with efficient connections and good food options.' },
  'dtw': { code: 'DTW', name: 'Detroit Metropolitan Airport', city: 'Detroit', state: 'MI', description: 'Major Delta hub known for the iconic tunnel between terminals with light show. McNamara Terminal is one of the largest in North America.' },
  'fll': { code: 'FLL', name: 'Fort Lauderdale-Hollywood International Airport', city: 'Fort Lauderdale', state: 'FL', description: 'Major South Florida airport and Spirit Airlines hub. Often more affordable than MIA for Miami-area travel.' },
  'san': { code: 'SAN', name: 'San Diego International Airport', city: 'San Diego', state: 'CA', description: 'Single-runway airport right next to downtown. Beautiful views of the bay but can experience delays during busy periods.' },
  'dca': { code: 'DCA', name: 'Ronald Reagan Washington National Airport', city: 'Washington', state: 'DC', description: 'The close-in airport for Washington, DC, connected to downtown by Metro. Slot- and perimeter-restricted, so it skews toward short- and medium-haul domestic flights.' },
  'iad': { code: 'IAD', name: 'Washington Dulles International Airport', city: 'Washington', state: 'DC', description: "United's mid-Atlantic hub and the DC area's main long-haul international gateway. Famous for its mobile lounges and a long walk between the main terminal and the midfield concourses." },
  'bwi': { code: 'BWI', name: 'Baltimore/Washington International Thurgood Marshall Airport', city: 'Baltimore', state: 'MD', description: 'A Southwest Airlines stronghold serving the Baltimore-Washington region. Often the cheapest of the three DC-area airports, with easy Amtrak and MARC rail connections.' },
  'tpa': { code: 'TPA', name: 'Tampa International Airport', city: 'Tampa', state: 'FL', description: 'Consistently rated one of the easiest big airports in the US thanks to its hub-and-spoke shuttle design. Gateway to Florida\'s Gulf Coast beaches.' }
};

export default function AirportGuidePage() {
  const { airportCode } = useParams();
  const airport = airportData[airportCode?.toLowerCase()] || additionalAirports[airportCode?.toLowerCase()];
  
  if (!airport) {
    return <NotFoundPage />;
  }

  const isDetailedAirport = !!airportData[airportCode?.toLowerCase()];

  // Build SEO data for this airport guide
  const airportName = airport.name || `${airport.code} Airport`;
  const airportCity = airport.city || '';
  const seoTitle = `${airport.code} Airport Crowd Levels & Wait Times — ${airportName}`;
  const seoDescription = `Live crowd predictions, security wait times, parking, terminals, and arrival‑time recommendations for ${airportName} (${airport.code}) in ${airportCity}. Plan smarter departures with real‑time flight data.`;
  const seoKeywords = `${airport.code} airport, ${airportName}, ${airportCity} airport, ${airport.code} security wait, ${airport.code} crowds, when to arrive at ${airport.code}, ${airport.code} parking`;
  const breadcrumb = buildBreadcrumb([
    { name: 'Home', url: '/' },
    { name: 'Airport Guides', url: '/airports' },
    { name: `${airport.code} – ${airportCity}`, url: `/airports/${airport.code.toLowerCase()}` },
  ]);
  const placeLd = {
    '@context': 'https://schema.org',
    '@type': 'Airport',
    name: airportName,
    iataCode: airport.code,
    address: airportCity ? { '@type': 'PostalAddress', addressLocality: airportCity, addressRegion: airport.state || undefined } : undefined,
    description: airport.description,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        path={`/airports/${airport.code.toLowerCase()}`}
        keywords={seoKeywords}
        structuredData={[breadcrumb, placeLd]}
      />
      {/* Header */}
      <SiteHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-500 to-blue-600 text-white py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm mb-4">
              Airport Guide
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2">
              {airport.code}
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 mb-2">
              {airport.name}
            </p>
            <p className="text-blue-200 flex items-center justify-center gap-1">
              <MapPin className="w-4 h-4" />
              {airport.city}, {airport.state}
            </p>
            <div className="mt-4 flex justify-center">
              <AirportDelayBadge code={airport.code} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto mb-4">
          <Breadcrumbs
            items={[
              { name: 'Home', to: '/' },
              { name: 'Airport Guides', to: '/airports' },
              { name: `${airport.code} — ${airport.city}` },
            ]}
          />
        </div>
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Description */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <p className="text-slate-600 leading-relaxed">
                {airport.description}
              </p>
              
              {/* Quick CTA */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">Planning to fly from {airport.code}?</p>
                <Link 
                  to={`/?airport=${airport.code}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                  <Clock className="w-4 h-4" />
                  Check Current Busyness
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Flagship original analysis — unique content for top hubs */}
          {(() => {
            const flagship = getFlagshipAnalysis(airport.code);
            if (!flagship) return null;
            return (
              <Card className="border-slate-200 bg-white shadow-sm" data-testid={`flagship-analysis-${airport.code}`}>
                <CardContent className="p-5 sm:p-7">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
                    Insider analysis
                  </span>
                  <h2 className="heading text-xl sm:text-2xl font-semibold text-slate-900 mt-3 mb-4 leading-snug">
                    {flagship.headline}
                  </h2>
                  <div className="space-y-3 text-[15px] text-slate-700 leading-relaxed">
                    {flagship.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                  {flagship.insider_tip && (
                    <div className="mt-5 p-4 border-l-2 border-amber-400 bg-amber-50/50 rounded-r">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">Insider tip</p>
                      <p className="text-sm text-slate-700">{flagship.insider_tip}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Data-driven insights section (all airports) */}
          <AirportInsightsSection airportCode={airport.code} />

          <NearbyAirports code={airport.code} />

          {/* SpotHero parking widget (all airports — affiliate revenue) */}
          <ParkingWidget
            airportCode={airport.code}
            airportName={airport.name}
            city={airport.city}
          />

          {isDetailedAirport && (
            <>
              {/* Terminals */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Terminal className="w-5 h-5 text-blue-500" />
                    Terminals & Airlines
                  </h2>
                  <div className="space-y-3">
                    {airport.terminals.map((terminal, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                          <h3 className="font-medium text-slate-800">{terminal.name}</h3>
                          <span className="text-sm text-blue-600">{terminal.airlines}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{terminal.tips}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Security Tips */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    Security & TSA Tips
                  </h2>
                  <ul className="space-y-2">
                    {airport.securityTips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-600">
                        <span className="text-blue-500 mt-1">•</span>
                        <span className="text-sm sm:text-base">{tip}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Average Wait Times */}
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-medium text-slate-700 mb-3 text-sm">Average Security Wait Times</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                      <div className="p-2 bg-green-50 rounded">
                        <p className="text-xs text-green-600">Low</p>
                        <p className="font-medium text-green-700">{airport.averageSecurityWait.low}</p>
                      </div>
                      <div className="p-2 bg-yellow-50 rounded">
                        <p className="text-xs text-yellow-600">Medium</p>
                        <p className="font-medium text-yellow-700">{airport.averageSecurityWait.medium}</p>
                      </div>
                      <div className="p-2 bg-orange-50 rounded">
                        <p className="text-xs text-orange-600">High</p>
                        <p className="font-medium text-orange-700">{airport.averageSecurityWait.high}</p>
                      </div>
                      <div className="p-2 bg-red-50 rounded">
                        <p className="text-xs text-red-600">Extreme</p>
                        <p className="font-medium text-red-700">{airport.averageSecurityWait.extreme}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Parking & Transportation */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Car className="w-5 h-5 text-blue-500" />
                    Parking & Transportation
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium text-slate-700 mb-2">Parking Options</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between p-2 bg-slate-50 rounded">
                          <span className="text-slate-600">Economy</span>
                          <span className="text-slate-800">{airport.parking.economy}</span>
                        </div>
                        <div className="flex justify-between p-2 bg-slate-50 rounded">
                          <span className="text-slate-600">Daily</span>
                          <span className="text-slate-800">{airport.parking.daily}</span>
                        </div>
                        <div className="flex justify-between p-2 bg-slate-50 rounded">
                          <span className="text-slate-600">Hourly</span>
                          <span className="text-slate-800">{airport.parking.hourly}</span>
                        </div>
                      </div>
                      <p className="text-sm text-blue-600 mt-2 p-2 bg-blue-50 rounded">
                        💡 {airport.parking.tips}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-slate-700 mb-2">Public Transportation</h3>
                      <ul className="space-y-1">
                        {airport.transportation.map((option, idx) => (
                          <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                            <span className="text-blue-500">•</span>
                            {option}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dining */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Utensils className="w-5 h-5 text-blue-500" />
                    Dining & Food
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {airport.dining.map((restaurant, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <h3 className="font-medium text-slate-800">{restaurant.name}</h3>
                        <p className="text-xs text-blue-600">{restaurant.terminal} • {restaurant.type}</p>
                        <p className="text-sm text-slate-500 mt-1">{restaurant.note}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Amenities */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-blue-500" />
                    Amenities
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {airport.amenities.map((amenity, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Nearby Hotels */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4">
                    Nearby Hotels
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {airport.nearbyHotels.map((hotel, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm">
                        {hotel}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* CTA */}
          <div className="text-center py-6">
            <p className="text-slate-600 mb-4">Ready to check how busy {airport.code} will be?</p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plane className="w-5 h-5" />
              Check Airport Busyness
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6 sm:py-8 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-4">
            <Link to="/" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/airports" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">All Airports</Link>
            <Link to="/blog" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/faq" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">FAQ</Link>
            <Link to="/about" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">About</Link>
          </div>
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Airport Busyness Predictor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
